import { useCallback, useEffect, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material"
import { toast } from "react-toastify"

import type { IGetCommentResponse } from "../../api/types"
import { useLazyGetCommentThreadPageQuery } from "../../api/projects"
import { useLazyGetGrowthToolCommentThreadPageQuery } from "../../api/growthTools"
import { useLazyGetSocialOrderCommentThreadPageQuery } from "../../api/socialOrders"
import { mapApiNodeToFlatThreadReply } from "../CommentsSection/helpers/commentDataMappers"
import { sortCommentsByPublishedAtAsc } from "../CommentsSection/helpers/mergeCommentBranch"
import { ReplyItem } from "../CommentsSection/ChildrenComponents/ReplyItem"
import { ReplyForm } from "../CommentsSection/ChildrenComponents/ReplyForm"
import type { CommentEditState } from "../CommentsSection/ChildrenComponents/CommentEditableText"
import { HIGHLIGHT_HOLD_MS } from "../CommentsSection/helpers/commentHighlightSx"
import { CURSOR_LIST_PAGE_SIZE } from "../../consts/api"

export type SubmitCommentDetailedResult =
    | { ok: true; created?: IGetCommentResponse }
    | { ok: false }

export type CommentThreadComposerInitialTarget = {
    parentId: number
    firstName: string
    lastName: string
}

export type CommentThreadModalBodyProps = {
    projectId?: number
    growthToolId?: number
    socialOrderId?: number
    commentType: string
    anchorL2Id: number
    excerpt: string
    currentUserId: number | null
    submitCommentDetailed: (
        payload: { text: string; parentId?: number }
    ) => Promise<SubmitCommentDetailedResult>
    onUpdateComment: (commentId: number, text: string) => Promise<boolean>
    isUpdatingComment: boolean
    isSubmittingComment: boolean
    /** Сразу открыть блок ответа (на узел-тред L2, которого нет в плоском списке). */
    initialComposerTarget?: CommentThreadComposerInitialTarget | null
    highlightCommentId?: number
}

type ThreadForm = {
    replyText: string
}

const THREAD_HIGHLIGHT_MAX_EXTRA_PAGES = 4

export function CommentThreadModalBody({
    projectId,
    growthToolId,
    socialOrderId,
    commentType,
    anchorL2Id,
    excerpt,
    currentUserId,
    submitCommentDetailed,
    onUpdateComment,
    isUpdatingComment,
    isSubmittingComment,
    initialComposerTarget,
    highlightCommentId,
}: CommentThreadModalBodyProps) {
    const methods = useForm<ThreadForm>({
        defaultValues: { replyText: "" },
    })

    const [fetchProjectThread] = useLazyGetCommentThreadPageQuery()
    const [fetchGrowthToolThread] = useLazyGetGrowthToolCommentThreadPageQuery()
    const [fetchSocialOrderThread] = useLazyGetSocialOrderCommentThreadPageQuery()
    const [rows, setRows] = useState<IGetCommentResponse[]>([])
    const [nextCursor, setNextCursor] = useState<string | null>(null)
    const [loadingInitial, setLoadingInitial] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [activeReplyTarget, setActiveReplyTarget] = useState<{
        firstName: string
        lastName: string
        parentId: number
    } | null>(() => {
        if (!initialComposerTarget?.parentId) return null
        return {
            parentId: initialComposerTarget.parentId,
            firstName: initialComposerTarget.firstName,
            lastName: initialComposerTarget.lastName,
        }
    })
    const [editState, setEditState] = useState<CommentEditState>(null)
    const [modalHighlightId, setModalHighlightId] = useState<number | null>(null)
    const threadHighlightExtraPagesLoadedRef = useRef(0)
    const highlightNotFoundToastShownRef = useRef(false)

    const cancelEdit = useCallback(() => setEditState(null), [])

    const setDraft = useCallback((str: string) => {
        setEditState((prev) => (prev ? { ...prev, draft: str } : null))
    }, [])

    const handleBeginEdit = useCallback((commentId: number, originalText: string) => {
        setActiveReplyTarget(null)
        methods.setValue("replyText", "")
        setEditState({ commentId, original: originalText, draft: originalText })
    }, [methods])

    const onUpdateInThread = useCallback(
        async (commentId: number, text: string) => {
            const ok = await onUpdateComment(commentId, text)
            if (ok) {
                setRows((prev) =>
                    prev.map((n) => (n.id === commentId ? { ...n, text, edited: true } : n))
                )
            }
            return ok
        },
        [onUpdateComment]
    )

    const confirmSave = useCallback(async () => {
        if (!editState) return
        const trimmed = editState.draft.trim()
        if (!trimmed) return
        if (trimmed === editState.original.trim()) {
            cancelEdit()
            return
        }
        const ok = await onUpdateInThread(editState.commentId, trimmed)
        if (ok) cancelEdit()
    }, [editState, onUpdateInThread, cancelEdit])

    const loadPage = useCallback(
        async (cursor: string | null, append: boolean) => {
            if (append) setLoadingMore(true)
            else setLoadingInitial(true)
            try {
                const page = growthToolId != null
                    ? await fetchGrowthToolThread({
                        growthToolId,
                        type: commentType,
                        parentId: anchorL2Id,
                        cursor: cursor ?? undefined,
                        limit: CURSOR_LIST_PAGE_SIZE,
                    }).unwrap()
                    : socialOrderId != null
                      ? await fetchSocialOrderThread({
                          socialOrderId,
                          type: commentType,
                          parentId: anchorL2Id,
                          cursor: cursor ?? undefined,
                          limit: CURSOR_LIST_PAGE_SIZE,
                      }).unwrap()
                      : projectId != null
                      ? await fetchProjectThread({
                          projectId,
                          type: commentType,
                          parentId: anchorL2Id,
                          cursor: cursor ?? undefined,
                          limit: CURSOR_LIST_PAGE_SIZE,
                      }).unwrap()
                      : null
                if (!page) {
                    throw new Error("Missing comment thread entity id")
                }
                setNextCursor(page.nextCursor)
                setRows((prev) => {
                    if (!append) return sortCommentsByPublishedAtAsc([...page.items])
                    const byId = new Map<number, IGetCommentResponse>()
                    prev.forEach((n) => byId.set(n.id, n))
                    page.items.forEach((n) => byId.set(n.id, n))
                    return sortCommentsByPublishedAtAsc(Array.from(byId.values()))
                })
            } catch {
                toast.error("Не удалось загрузить ответы")
            } finally {
                setLoadingInitial(false)
                setLoadingMore(false)
            }
        },
        [fetchProjectThread, fetchGrowthToolThread, fetchSocialOrderThread, projectId, growthToolId, socialOrderId, commentType, anchorL2Id]
    )

    useEffect(() => {
        void loadPage(null, false)
    }, [loadPage])

    useEffect(() => {
        threadHighlightExtraPagesLoadedRef.current = 0
        highlightNotFoundToastShownRef.current = false
    }, [highlightCommentId, anchorL2Id])

    useEffect(() => {
        if (!highlightCommentId) return
        if (loadingInitial || loadingMore) return

        const targetId = highlightCommentId
        const inRows = rows.some((r) => r.id === targetId)

        if (inRows) {
            const el = document.getElementById(`comment-${targetId}`)
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" })
                setModalHighlightId(targetId)
            }
            return
        }

        if (nextCursor && threadHighlightExtraPagesLoadedRef.current < THREAD_HIGHLIGHT_MAX_EXTRA_PAGES) {
            threadHighlightExtraPagesLoadedRef.current += 1
            void loadPage(nextCursor, true)
            return
        }

        if (highlightNotFoundToastShownRef.current) return
        highlightNotFoundToastShownRef.current = true
        toast.error("Комментарий в переписке ниже")
    }, [highlightCommentId, loadingInitial, loadingMore, rows, nextCursor, loadPage])

    useEffect(() => {
        if (modalHighlightId == null) return
        const t = window.setTimeout(() => setModalHighlightId(null), HIGHLIGHT_HOLD_MS)
        return () => window.clearTimeout(t)
    }, [modalHighlightId])

    const handleReplyClick = useCallback((parentId: number, firstName: string, lastName: string) => {
        setActiveReplyTarget({ firstName, lastName, parentId })
    }, [])

    const handleDesktopReplySubmit = async () => {
        if (!activeReplyTarget) return
        const text = methods.getValues("replyText")
        const result = await submitCommentDetailed({ text, parentId: activeReplyTarget.parentId })
        if (result.ok) {
            setActiveReplyTarget(null)
            methods.setValue("replyText", "")
            if (result.created) {
                setRows((prev) => sortCommentsByPublishedAtAsc([...prev, result.created as IGetCommentResponse]))
            }
        }
    }

    const flatUi = rows.map(mapApiNodeToFlatThreadReply)
    const commentReactionCache = growthToolId != null
        ? {
            kind: "growthTool" as const,
            growthToolId,
            type: commentType,
            threadParentId: anchorL2Id,
        }
        : socialOrderId != null
          ? {
              kind: "socialOrder" as const,
              socialOrderId,
              type: commentType,
              threadParentId: anchorL2Id,
          }
        : projectId != null
          ? {
              projectId,
              type: commentType,
              threadParentId: anchorL2Id,
          }
          : undefined

    return (
        <FormProvider {...methods}>
            <Stack gap={2} sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {excerpt.trim().length > 0 ? (
                    <Typography
                        variant="black-14-normal"
                        component="div"
                        title={excerpt}
                        sx={{
                            flexShrink: 0,
                            mb: -0.5,
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.4,
                            minHeight: "min-content",
                        }}
                    >
                        {excerpt}
                    </Typography>
                ) : null}
                <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }} gap={2}>
                    {loadingInitial ? (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress size={28} />
                        </Stack>
                    ) : flatUi.length === 0 ? (
                        <Typography variant="black-14-light">Пока нет ответов.</Typography>
                    ) : (
                        flatUi.map((reply) => (
                            <ReplyItem
                                key={reply.id}
                                reply={reply}
                                replyDepth={0}
                                flatList
                                onReplyTo={handleReplyClick}
                                currentUserId={currentUserId}
                                editState={editState}
                                onBeginEdit={handleBeginEdit}
                                cancelEdit={cancelEdit}
                                setDraft={setDraft}
                                confirmSave={confirmSave}
                                isUpdatingComment={isUpdatingComment}
                                commentReactionCache={commentReactionCache}
                                onCommentReactionSuccess={(node) => {
                                    setRows((prev) =>
                                        prev.map((n) =>
                                            n.id === node.id
                                                ? { ...n, ...node, children: n.children }
                                                : n
                                        )
                                    )
                                }}
                                highlightCommentId={modalHighlightId}
                            />
                        ))
                    )}
                    {!loadingInitial && nextCursor ? (
                        <Button
                            variant="text"
                            disabled={loadingMore}
                            onClick={() => void loadPage(nextCursor, true)}
                            sx={{ alignSelf: "flex-start", textTransform: "none" }}
                        >
                            {loadingMore ? <CircularProgress size={20} /> : "Показать ещё"}
                        </Button>
                    ) : null}
                </Stack>
                {activeReplyTarget ? (
                    <Box sx={{ flexShrink: 0, width: "100%" }}>
                        <ReplyForm
                            activeReplyTarget={activeReplyTarget}
                            onSend={handleDesktopReplySubmit}
                            isSubmitting={isSubmittingComment}
                            onClose={() => {
                                setActiveReplyTarget(null)
                                methods.setValue("replyText", "")
                            }}
                        />
                    </Box>
                ) : null}
            </Stack>
        </FormProvider>
    )
}
